import { captureException } from '@/utils/sentry';
import {
  DataProxy,
  gql,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  FILTER_STATUS_SETS,
  INBOUND_LIST_FILTERS,
  InboundListFilter,
} from './order-inbounds-order-list/types';
import {
  InboundListQueryFilters,
  useOrderInboundsStore,
} from '@/stores/order-inbounds-store';
import {
  OrderInbound_AssignSmartOrderMutation,
  OrderInbound_AssignSmartOrderMutationVariables,
  OrderInbound_GetSmartOrderAssignableEmployeesQuery,
  OrderInbound_GetSmartOrderAssignableEmployeesQueryVariables,
  OrderInbound_GrantSmartOrderRestaurantAccessMutation,
  OrderInbound_GrantSmartOrderRestaurantAccessMutationVariables,
  OrderInbound_UpdateFulfillmentOptionsMutation,
  OrderInbound_UpdateFulfillmentOptionsMutationVariables,
  OrderInbound_UpdateItemNotesMutation,
  OrderInbound_UpdateItemNotesMutationVariables,
  OrderInbound_UpdateOrderNotesMutation,
  OrderInbound_UpdateOrderNotesMutationVariables,
  OrderInbound_UpdatePoNumberMutation,
  OrderInbound_UpdatePoNumberMutationVariables,
  OrderInbound_UpdateQuantityMutation,
  OrderInbound_UpdateQuantityMutationVariables,
  OrderInbound_UpdateSmartOrderAssigneeMutation,
  OrderInbound_UpdateSmartOrderAssigneeMutationVariables,
  OrderInbounds_ListAssigneesQuery,
  OrderInbounds_ListAssigneesQueryVariables,
  OrderInbounds_ListHistoryQuery,
  OrderInbounds_ListHistoryQueryVariables,
  OrderInbounds_ListLiveUpdateQuery,
  OrderInbounds_ListLiveUpdateQueryVariables,
  OrderInbounds_ListQuery,
  OrderInbounds_ListQueryVariables,
  OrderInbounds_MemorizeOrderMutation,
  OrderInbounds_MemorizeOrderMutationVariables,
  OrderInbounds_SearchTermCorrectionsQuery,
  OrderInbounds_SearchTermCorrectionsQueryVariables,
  OrderInput_CreateSearchTermCorrectionMutation,
  OrderInput_CreateSearchTermCorrectionMutationVariables,
  OrderInput_UpdateSmartOrderItemMutation,
  OrderInput_UpdateSmartOrderItemMutationVariables,
  SmartOrderStatus as SchemaSmartOrderStatus,
  Smart_Order_Bool_Exp,
  SmartOrderInboundHistoryOrderBy,
} from '@/types';
import { partnerSupplier } from '@/utils/environment';
import { SALT_BASE_ITEM_FIELDS_FRAGMENT } from '@/contexts/item-context-fragments';
import { SmartOrderStatus } from '@/enums';
import { useCallback, useMemo } from 'react';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { USER_SUPPLIER_EMPLOYEE_OVERRIDES_FRAGMENT } from '@/contexts/user-context-queries';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

interface GetSmartOrderQueryWhereParams {
  getUnreadOnly: boolean;
  unknownContactTriageEnabled: boolean;
}

export const getSmartOrderQueryWhere = ({
  getUnreadOnly,
  unknownContactTriageEnabled,
}: GetSmartOrderQueryWhereParams): Smart_Order_Bool_Exp => {
  const viewableStatuses: SmartOrderStatus[] = [
    SmartOrderStatus.PENDING,
    SmartOrderStatus.NO_RESULTS,
  ];

  if (unknownContactTriageEnabled) {
    viewableStatuses.push(SmartOrderStatus.UNKNOWN_CONTACT);
  }

  return {
    ...(getUnreadOnly ? { last_viewed_at: { _is_null: true } } : {}),
    status: { _in: viewableStatuses },
    submit_type: { _eq: 'DISTRIBUTOR' },
    supplier: {
      business_organization_uuid: {
        _eq: partnerSupplier.businessOrganizationUUID,
      },
    },
  };
};

const useSmartOrderQueryWhere = (getUnreadOnly = false) => {
  const { unknownContactTriageEnabled } = useOrderInbounds();
  return useMemo(
    () =>
      getSmartOrderQueryWhere({
        getUnreadOnly,
        unknownContactTriageEnabled,
      }),
    [getUnreadOnly, unknownContactTriageEnabled],
  );
};

export const useOrderInboundListQueryVariables = () => {
  const smartOrderQueryWhere = useSmartOrderQueryWhere();

  return useMemo<OrderInbounds_ListQueryVariables>(() => {
    return {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      where: smartOrderQueryWhere,
    };
  }, [smartOrderQueryWhere]);
};

export const useIsInboundHistoryEnabled = () =>
  useSupplierDynamicConfigFlag('smart_order_config', 'inbound_history_enabled');

export const INBOUND_HISTORY_PAGE_SIZE = 25;

export const INBOUND_HISTORY_VARIABLES: Record<
  InboundListFilter,
  OrderInbounds_ListHistoryQueryVariables
> = Object.fromEntries(
  INBOUND_LIST_FILTERS.map((filter) => [
    filter,
    {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      limit: INBOUND_HISTORY_PAGE_SIZE,
      offset: 0,
      statuses: Array.from(
        FILTER_STATUS_SETS[filter],
      ) as unknown as SchemaSmartOrderStatus[],
    },
  ]),
) as Record<InboundListFilter, OrderInbounds_ListHistoryQueryVariables>;

/**
 * CLOSEST_CUTOFF orders by time-left-until-cutoff, which the backend can only
 * compute for orders still in the triage queue. The Closed tab is
 * terminal-status-only, so its query is clamped back to the default ordering.
 *
 * The user never sees this substitution -- the option is rendered disabled on
 * that tab -- but the clamp is not cosmetic: all three tab queries stay
 * permanently mounted to keep the pill counts live, so without it the
 * Closed query would error the moment the sort was applied on any tab and
 * its count would blank out.
 */
const clampSortForFilter = (
  filter: InboundListFilter,
  sortBy: SmartOrderInboundHistoryOrderBy,
): SmartOrderInboundHistoryOrderBy =>
  filter === InboundListFilter.Closed &&
  sortBy === SmartOrderInboundHistoryOrderBy.ClosestCutoff
    ? SmartOrderInboundHistoryOrderBy.NewestFirst
    : sortBy;

/**
 * True when the list is narrowed or reordered by anything the user chose -- a
 * search term, an assignee selection, or a non-default sort.
 */
export const hasNonDefaultInboundFilters = (
  queryFilters: InboundListQueryFilters,
): boolean =>
  Boolean(queryFilters.searchTerm) ||
  queryFilters.assignedEmployeeIds.length > 0 ||
  queryFilters.includeUnassigned ||
  queryFilters.sortBy !== SmartOrderInboundHistoryOrderBy.NewestFirst;

/**
 * The filters a given tab actually queries with: the user's filters with the
 * sort clamped for that tab. Callers deciding between a tab's base and
 * filtered queries must make that call on this, not on the raw store value --
 * for the Closed tab, a lone CLOSEST_CUTOFF sort clamps back to the
 * default order, leaving nothing to filter by, so that tab keeps reading its
 * base query (and its base cache entry) rather than issuing a second query
 * whose variables would collide with the base entry's cache key.
 */
export const getEffectiveInboundQueryFilters = (
  filter: InboundListFilter,
  queryFilters: InboundListQueryFilters,
): InboundListQueryFilters => ({
  ...queryFilters,
  sortBy: clampSortForFilter(filter, queryFilters.sortBy),
});

// Filtering runs as its own set of queries rather than as extra variables on
// the three base ones above. Keeping the user's filters out of
// INBOUND_HISTORY_VARIABLES is the whole point: the base cache entries are
// never re-keyed, so they stay warm and fully populated while filters are
// applied, and clearing them is a synchronous swap back to the base entries
// rather than a refetch.
//
// Only ever called when the tab's effective filters (see
// getEffectiveInboundQueryFilters) are non-default -- an all-default filter
// set reads the base entries instead, so there's no "defaults spelled out vs
// absent" cache key to reconcile here. Each individual filter still collapses
// to `undefined` when left at its default so the cache doesn't fork a
// separate entry per combination of untouched filters.
export const getInboundHistoryFilteredVariables = (
  filter: InboundListFilter,
  queryFilters: InboundListQueryFilters,
): OrderInbounds_ListHistoryQueryVariables => {
  const { sortBy } = getEffectiveInboundQueryFilters(filter, queryFilters);

  return {
    ...INBOUND_HISTORY_VARIABLES[filter],
    assignedEmployeeIds: queryFilters.assignedEmployeeIds.length
      ? queryFilters.assignedEmployeeIds
      : undefined,
    includeUnassigned: queryFilters.includeUnassigned || undefined,
    orderBy:
      sortBy === SmartOrderInboundHistoryOrderBy.NewestFirst
        ? undefined
        : sortBy,
    searchTerm: queryFilters.searchTerm || undefined,
  };
};

const SMART_ORDER_SEARCH_INPUTS_FRAGMENT = gql`
  fragment SmartOrderSearchInputsFields on smart_order_search_input {
    index
    intent
    item_notes
    matched_image_location
    matched_text
    original_pack_unit
    original_quantity
    pack_unit
    quantity
    requested_item_code
    requested_item_name
    search_term: smart_order_search_term {
      term: effective_search_term
      original_term: original_search_term
      status
      translations
      uuid
      variants_pack @skip(if: $skipSmartOrderVariantPack) {
        ...ItemsContext_VariantsPackFields
      }
    }
    smart_order_uuid
    translations
    uuid
  }

  ${VARIANTS_PACK_FRAGMENT}
  ${SALT_BASE_ITEM_FIELDS_FRAGMENT}
`;

export const ORDER_INBOUND_ASSIGNED_EMPLOYEE_FIELDS_FRAGMENT = gql`
  fragment OrderInboundAssignedEmployeeFields on employees {
    name
    supplier_employee_overrides(
      where: {
        supplier: {
          business_organization_uuid: { _eq: $businessOrganizationUUID }
        }
      }
    ) {
      uuid
      name
    }
    uuid
  }

  ${USER_SUPPLIER_EMPLOYEE_OVERRIDES_FRAGMENT}
`;

export const SMART_ORDER_FIELDS_FRAGMENT = gql`
  fragment SmartOrderSegmentFields on smart_order_segment {
    attachment_index
    matched_text
    smart_order_search_input_uuid
    sort_order
    start_seconds
    translations
    uuid
  }
  fragment SmartOrderFields on smart_order {
    assigned_employee {
      ...OrderInboundAssignedEmployeeFields
    }
    attachments
    created_at
    created_by {
      name
      contact_info {
        email
        login_email
        phone_number
        uuid
      }
      supplier_employee_overrides(
        where: {
          supplier: {
            business_organization_uuid: { _eq: $businessOrganizationUUID }
          }
        }
      ) {
        ...UserContextUserSupplierEmployeeOverrides
      }
      uuid
    }
    email_address
    fulfillment_at
    fulfillment_type
    merge_uuid
    order {
      notes
      uuid
    }
    order_notes
    order_uuid
    original_fulfillment_at
    original_sent_at
    po_number
    phone_number
    processing_status
    restaurant {
      name
      uuid
    }
    search_inputs: smart_order_search_inputs(order_by: { index: asc }) {
      ...SmartOrderSearchInputsFields
    }
    search_text
    smart_order_segments(order_by: { sort_order: asc }) {
      ...SmartOrderSegmentFields
    }
    source
    status
    subject
    supplier_uuid
    thread_id
    translations
    updated_at
    uuid
    voicemail_word_timestamps
  }

  ${SMART_ORDER_SEARCH_INPUTS_FRAGMENT}
  ${ORDER_INBOUND_ASSIGNED_EMPLOYEE_FIELDS_FRAGMENT}
`;

export const ORDER_INBOUNDS_LIST_SUBSCRIPTION = gql`
  fragment OrderInboundListSubscriptionFields on employee_smart_orders {
    assigned_employee_name
    assigned_employee_uuid
    merge_uuid
    processing_status
    restaurant_name
    restaurant_uuid
    smart_order_uuid
    status
    supplier_employee_override_name
    supplier_employee_override_uuid
  }

  subscription OrderInbounds_ListSubscription(
    $lastCreatedAt: timestamptz!
    $employeeUUID: uuid!
    $businessOrganizationUUID: uuid!
    $orderUUIDs: [uuid!]!
  ) {
    employee_smart_orders(
      where: {
        _or: [
          { created_at: { _gt: $lastCreatedAt } }
          { smart_order_uuid: { _in: $orderUUIDs } }
        ]
        employee_uuid: { _eq: $employeeUUID }
        business_organization_uuid: { _eq: $businessOrganizationUUID }
      }
    ) {
      ...OrderInboundListSubscriptionFields
    }
  }
`;

const ORDER_INBOUNDS_LIST_FIELDS = gql`
  fragment OrderInboundListSearchInputFields on smart_order_search_input {
    matched_text
    pack_unit
    quantity
    search_term: smart_order_search_term {
      term: effective_search_term
      status
      translations
      uuid
    }
    translations
    uuid
  }
  fragment OrderInboundListFields on smart_order {
    assigned_employee {
      ...OrderInboundAssignedEmployeeFields
    }
    attachments
    created_at
    created_by {
      name
      contact_info {
        email
        login_email
        phone_number
        uuid
      }
      supplier_employee_overrides(
        where: {
          supplier: {
            business_organization_uuid: { _eq: $businessOrganizationUUID }
          }
        }
      ) {
        ...UserContextUserSupplierEmployeeOverrides
      }
      uuid
    }
    email_address
    fulfillment_at
    fulfillment_type
    merge_uuid
    order_uuid
    phone_number
    processing_status
    restaurant {
      name
      uuid
    }
    search_inputs: smart_order_search_inputs(order_by: { index: asc }) {
      ...OrderInboundListSearchInputFields
    }
    search_text
    source
    status
    supplier_uuid
    thread_id
    translations
    updated_at
    uuid
  }

  ${ORDER_INBOUND_ASSIGNED_EMPLOYEE_FIELDS_FRAGMENT}
`;

export const ORDER_INBOUNDS_THREAD_MESSAGE_RELS_SUBSCRIPTION = gql`
  subscription OrderInbounds_ThreadMessageRels($threadIds: [String!]!) {
    smart_order_message_rel(where: { thread_id: { _in: $threadIds } }) {
      message_id
      smart_order_uuid
      thread_id
      uuid
    }
  }
`;

export const ORDER_INBOUNDS_LIST_QUERY = gql`
  query OrderInbounds_List(
    $where: smart_order_bool_exp!
    $businessOrganizationUUID: uuid!
  ) {
    smart_order(where: $where, order_by: { created_at: desc }) {
      ...OrderInboundListFields
    }
  }

  ${ORDER_INBOUNDS_LIST_FIELDS}
`;

export const ORDER_INBOUNDS_LIST_LIVE_UPDATE_QUERY = gql`
  query OrderInbounds_ListLiveUpdate(
    $orderUUIDs: [uuid!]!
    $businessOrganizationUUID: uuid!
  ) {
    smart_order(where: { uuid: { _in: $orderUUIDs } }) {
      ...OrderInboundListFields
    }
  }

  ${ORDER_INBOUNDS_LIST_FIELDS}
`;

export const ORDER_INBOUNDS_LIST_HISTORY_QUERY = gql`
  query OrderInbounds_ListHistory(
    $businessOrganizationUUID: uuid!
    $statuses: [SmartOrderStatus!]!
    $limit: Int
    $offset: Int
    $searchTerm: String
    $assignedEmployeeIds: [uuid!]
    $includeUnassigned: Boolean
    $orderBy: SmartOrderInboundHistoryOrderBy
  ) {
    getSmartOrderInboundHistory(
      business_organization_id: $businessOrganizationUUID
      statuses: $statuses
      limit: $limit
      offset: $offset
      search_term: $searchTerm
      assigned_employee_ids: $assignedEmployeeIds
      include_unassigned: $includeUnassigned
      order_by: $orderBy
    ) {
      total_count
      smart_orders {
        smart_order_id
        smart_order {
          ...OrderInboundListFields
        }
      }
    }
  }

  ${ORDER_INBOUNDS_LIST_FIELDS}
`;

export const ORDER_INBOUNDS_LIST_ASSIGNEES_QUERY = gql`
  query OrderInbounds_ListAssignees($businessOrganizationUUID: uuid!) {
    getSmartOrderInboundAssignees(
      business_organization_id: $businessOrganizationUUID
    ) {
      assignees {
        employee_id
        name
      }
    }
  }
`;

/**
 * Assignee options for the inbound list's filter panel: the employees who have
 * ever had an inbound assigned in this organization, not the whole employee
 * roster. Deliberately unscoped by the active status tab so the option list
 * stays put as the user moves between tabs.
 *
 * Skipped entirely when the history list is off, so the legacy query path
 * never issues it.
 */
export const useInboundAssigneeOptionsQuery = () => {
  const isInboundHistoryEnabled = useIsInboundHistoryEnabled();

  return useWrappedQuery<
    OrderInbounds_ListAssigneesQuery,
    OrderInbounds_ListAssigneesQueryVariables
  >(ORDER_INBOUNDS_LIST_ASSIGNEES_QUERY, {
    skip: !isInboundHistoryEnabled,
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
    },
  });
};

export const ORDER_INBOUNDS_DETAIL_QUERY = gql`
  fragment OrderInbounds_SwapSuggestionFields on VariantPackMatch {
    variant_pack {
      ...ItemsContext_VariantsPackFields
    }
    variant_pack_order_counts {
      days
      order_count
    }
  }
  fragment OrderInbounds_SearchInputFields on SmartOrderDetailsSearchInput {
    alternative_variant_pack_matches {
      ...OrderInbounds_SwapSuggestionFields
    }
    previous_variant_pack_matches {
      ...OrderInbounds_SwapSuggestionFields
    }
    search_input_id
    search_term_id
    variant_pack_id
    variant_pack_order_counts {
      days
      order_count
    }
  }

  query OrderInbounds_Detail(
    $locale: String!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $restaurantUUID: uuid!
    $businessOrganizationUUID: uuid!
    $skipGroupMemberships: Boolean!
    $smartOrderUUID: uuid!
    $mergeUUID: uuid
    $threadIds: [String!]
    $skipSmartOrderVariantPack: Boolean! = false
  ) {
    getSmartOrderDetails(
      smart_order_id: $smartOrderUUID
      merge_uuid: $mergeUUID
      thread_ids: $threadIds
    ) {
      search_inputs {
        ...OrderInbounds_SearchInputFields
      }
      smart_order {
        ...SmartOrderFields
      }
    }
  }

  ${SMART_ORDER_FIELDS_FRAGMENT}
  ${VARIANTS_PACK_FRAGMENT}
`;

export const ADD_SMART_ORDER_ITEM_MUTATION = gql`
  mutation OrderInbound_AddSmartOrderItem(
    $quantity: Int!
    $restaurantUUID: uuid!
    $searchTerm: String!
    $smartOrderUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $locale: String!
    $skipGroupMemberships: Boolean!
    $skipSmartOrderVariantPack: Boolean! = false
  ) {
    addSmartOrderItem(
      quantity: $quantity
      restaurant_id: $restaurantUUID
      search_term: $searchTerm
      supplier_id: $supplierUUID
      smart_order_id: $smartOrderUUID
      variant_pack_id: $variantPackUUID
    ) {
      smart_order_search_input {
        ...SmartOrderSearchInputsFields
      }
    }
  }

  ${SMART_ORDER_SEARCH_INPUTS_FRAGMENT}
`;

export const UPDATE_SMART_ORDER_ITEM_MUTATION = gql`
  mutation OrderInput_UpdateSmartOrderItem(
    $newSearchTerm: String!
    $newVariantPackUUID: uuid!
    $restaurantUUID: uuid!
    $smartOrderSearchInputUUID: uuid!
    $supplierUUID: uuid!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $locale: String!
    $rememberSelection: Boolean
    $skipGroupMemberships: Boolean!
    $recalculateQuantity: Boolean!
    $skipSmartOrderVariantPack: Boolean! = false
  ) {
    updateSearchInput(
      new_search_term: $newSearchTerm
      new_variant_pack_id: $newVariantPackUUID
      restaurant_id: $restaurantUUID
      smart_order_search_input_id: $smartOrderSearchInputUUID
      supplier_id: $supplierUUID
      remember: $rememberSelection
      recalculate_quantity: $recalculateQuantity
    ) {
      smart_order_search_input {
        ...SmartOrderSearchInputsFields
      }
    }
  }

  ${SMART_ORDER_SEARCH_INPUTS_FRAGMENT}
`;

export const CREATE_SEARCH_TERM_CORRECTION_MUTATION = gql`
  mutation OrderInput_CreateSearchTermCorrection(
    $oldSearchTermUUID: uuid!
    $restaurantUUID: uuid!
    $smartOrderUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    createSearchTermCorrection(
      old_search_term_id: $oldSearchTermUUID
      restaurant_id: $restaurantUUID
      smart_order_id: $smartOrderUUID
      supplier_id: $supplierUUID
      variant_pack_id: $variantPackUUID
    ) {
      search_term_correction_id
    }
  }
`;

export const SMART_ORDER_SEARCH_TERM_CORRECTIONS_QUERY = gql`
  query OrderInbounds_SearchTermCorrections(
    $smartOrderUUIDs: [uuid!]!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    smart_order_search_term_corrections(
      where: { smart_order_uuid: { _in: $smartOrderUUIDs } }
    ) {
      old_search_term_uuid
      smart_order_uuid
      uuid
      new_search_term {
        uuid
        variants_pack {
          ...ItemsContext_VariantsPackFields
        }
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const historyToListShape = (
  cached: OrderInbounds_ListHistoryQuery | null | undefined,
): OrderInbounds_ListQuery | undefined => {
  if (!cached?.getSmartOrderInboundHistory) {
    return undefined;
  }
  return {
    smart_order: cached.getSmartOrderInboundHistory.smart_orders
      .map((item) => item.smart_order)
      .filter(
        (
          smartOrder,
        ): smartOrder is OrderInbounds_ListQuery['smart_order'][number] =>
          Boolean(smartOrder),
      ),
  };
};

const mergeListShapeIntoHistoryQuery = (
  previousCached: OrderInbounds_ListHistoryQuery,
  updatedList: OrderInbounds_ListQuery['smart_order'],
  countDelta: number,
): OrderInbounds_ListHistoryQuery => {
  const previousItemsByUUID = new Map(
    previousCached.getSmartOrderInboundHistory.smart_orders
      .filter((item) => item.smart_order)
      .map((item) => [item.smart_order!.uuid, item]),
  );

  return {
    ...previousCached,
    getSmartOrderInboundHistory: {
      ...previousCached.getSmartOrderInboundHistory,
      smart_orders: updatedList.map((smartOrder) => {
        const previousItem = previousItemsByUUID.get(smartOrder.uuid);
        return {
          __typename:
            previousItem?.__typename ??
            ('SmartOrderInboundHistoryItem' as const),
          smart_order: smartOrder,
          smart_order_id: previousItem?.smart_order_id ?? smartOrder.uuid,
        };
      }),
      total_count:
        previousCached.getSmartOrderInboundHistory.total_count + countDelta,
    },
  };
};

// Targets one specific cache entry, named by its variables. Callers pass either
// a filter's base variables or its search variables -- the function no longer
// re-derives which of the two it means.
export const writeHistoryCacheForVariables = (
  client: DataProxy,
  variables: OrderInbounds_ListHistoryQueryVariables,
  updateFn: (cachedData: OrderInbounds_ListQuery) => OrderInbounds_ListQuery,
) => {
  const cachedQuery = client.readQuery<
    OrderInbounds_ListHistoryQuery,
    OrderInbounds_ListHistoryQueryVariables
  >({
    query: ORDER_INBOUNDS_LIST_HISTORY_QUERY,
    variables,
  });
  const normalized = historyToListShape(cachedQuery);
  if (cachedQuery && normalized) {
    const updatedList = updateFn(normalized).smart_order;
    const delta = updatedList.length - normalized.smart_order.length;
    client.writeQuery<
      OrderInbounds_ListHistoryQuery,
      OrderInbounds_ListHistoryQueryVariables
    >({
      data: mergeListShapeIntoHistoryQuery(cachedQuery, updatedList, delta),
      query: ORDER_INBOUNDS_LIST_HISTORY_QUERY,
      variables,
    });
  }
};

export const useUpdateInboundListQueryCache = () => {
  const isInboundHistoryEnabled = useIsInboundHistoryEnabled();
  const legacyQueryVariables = useOrderInboundListQueryVariables();
  const queryFilters = useOrderInboundsStore((store) => store.queryFilters);

  return useCallback(
    (
      client: DataProxy,
      updateFn: (
        cachedData: OrderInbounds_ListQuery,
      ) => OrderInbounds_ListQuery,
    ) => {
      try {
        if (isInboundHistoryEnabled) {
          for (const filter of INBOUND_LIST_FILTERS) {
            // Always patch the base entries -- they stay mounted and populated
            // whether or not filters are on, and are what the list swaps back
            // to the moment the filters clear.
            writeHistoryCacheForVariables(
              client,
              INBOUND_HISTORY_VARIABLES[filter],
              updateFn,
            );
            // Patch the visible filtered entries too, so their total_count
            // (the tab badges) tracks the same removals the base entries just
            // took. Per-tab effective filters, so a tab whose clamped filters
            // are all-default (and is therefore reading its base entry) isn't
            // patched twice.
            const effectiveFilters = getEffectiveInboundQueryFilters(
              filter,
              queryFilters,
            );
            if (hasNonDefaultInboundFilters(effectiveFilters)) {
              writeHistoryCacheForVariables(
                client,
                getInboundHistoryFilteredVariables(filter, effectiveFilters),
                updateFn,
              );
            }
          }
          return;
        }

        const cachedQuery = client.readQuery<
          OrderInbounds_ListQuery,
          OrderInbounds_ListQueryVariables
        >({
          query: ORDER_INBOUNDS_LIST_QUERY,
          variables: legacyQueryVariables,
        });
        if (cachedQuery?.smart_order) {
          client.writeQuery<
            OrderInbounds_ListQuery,
            OrderInbounds_ListQueryVariables
          >({
            data: updateFn(cachedQuery),
            query: ORDER_INBOUNDS_LIST_QUERY,
            variables: legacyQueryVariables,
          });
        }
      } catch (e) {
        captureException(e);
      }
    },
    [isInboundHistoryEnabled, legacyQueryVariables, queryFilters],
  );
};

export const UPDATE_INBOUND_QUANTITY_MUTATION = gql`
  mutation OrderInbound_UpdateQuantity(
    $smartOrderInputUUID: uuid!
    $quantity: float8!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipSmartOrderVariantPack: Boolean! = false
  ) {
    update_smart_order_search_input_by_pk(
      pk_columns: { uuid: $smartOrderInputUUID }
      _set: { quantity: $quantity }
    ) {
      ...SmartOrderSearchInputsFields
    }
  }
  ${SMART_ORDER_SEARCH_INPUTS_FRAGMENT}
`;

export const UPDATE_INBOUND_ITEM_NOTES_MUTATION = gql`
  mutation OrderInbound_UpdateItemNotes(
    $smartOrderInputUUID: uuid!
    $itemNotes: String
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipSmartOrderVariantPack: Boolean! = false
  ) {
    update_smart_order_search_input_by_pk(
      pk_columns: { uuid: $smartOrderInputUUID }
      _set: { item_notes: $itemNotes }
    ) {
      ...SmartOrderSearchInputsFields
    }
  }
  ${SMART_ORDER_SEARCH_INPUTS_FRAGMENT}
`;

export const ASSIGN_SMART_ORDER_MUTATION = gql`
  mutation OrderInbound_AssignSmartOrder(
    $employeeUUID: uuid!
    $restaurantUUID: uuid!
    $smartOrderUUID: uuid!
    $supplierUUID: uuid!
  ) {
    assignSmartOrder(
      assign_to_employee_id: $employeeUUID
      restaurant_id: $restaurantUUID
      smart_order_id: $smartOrderUUID
      supplier_id: $supplierUUID
    ) {
      smart_order {
        restaurant {
          name
          uuid
        }
        uuid
      }
      smart_order_id
    }
  }
`;

export const MEMORIZE_ORDER_MUTATION = gql`
  mutation OrderInbounds_MemorizeOrder(
    $orderUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    memorizeSmartOrder(
      smart_order_id: $orderUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      smart_order_id
    }
  }
`;

export const UPDATE_SMART_ORDER_ASSIGNEE_MUTATION = gql`
  mutation OrderInbound_UpdateSmartOrderAssignee(
    $employeeUUID: uuid
    $smartOrderUUID: uuid!
    $supplierUUID: uuid!
  ) {
    updateSmartOrderAssignee(
      employee_id: $employeeUUID
      smart_order_id: $smartOrderUUID
      supplier_id: $supplierUUID
    ) {
      assigned_employee_id
      smart_order_ids
    }
  }
`;

export const UPDATE_INBOUND_FULFILLMENT_OPTIONS_MUTATION = gql`
  mutation OrderInbound_UpdateFulfillmentOptions(
    $uuid: uuid!
    $openStatuses: [String!]!
    $fulfillment_at: timestamptz!
    $fulfillment_type: String!
    $updatedAt: timestamptz!
    $updatedBy: uuid!
  ) {
    update_smart_order(
      where: { uuid: { _eq: $uuid }, status: { _in: $openStatuses } }
      _set: {
        fulfillment_at: $fulfillment_at
        fulfillment_type: $fulfillment_type
        updated_at: $updatedAt
        updated_by_uuid: $updatedBy
      }
    ) {
      returning {
        uuid
        fulfillment_at
        fulfillment_type
      }
    }
  }
`;

export const UPDATE_INBOUND_PO_NUMBER_MUTATION = gql`
  mutation OrderInbound_UpdatePoNumber(
    $uuid: uuid!
    $openStatuses: [String!]!
    $po_number: String!
    $updatedAt: timestamptz!
    $updatedBy: uuid!
  ) {
    update_smart_order(
      where: { uuid: { _eq: $uuid }, status: { _in: $openStatuses } }
      _set: {
        po_number: $po_number
        updated_at: $updatedAt
        updated_by_uuid: $updatedBy
      }
    ) {
      returning {
        uuid
        po_number
      }
    }
  }
`;

export const UPDATE_INBOUND_ORDER_NOTES_MUTATION = gql`
  mutation OrderInbound_UpdateOrderNotes(
    $uuids: [uuid!]!
    $openStatuses: [String!]!
    $order_notes: String!
    $updatedAt: timestamptz!
    $updatedBy: uuid!
  ) {
    update_smart_order(
      where: { uuid: { _in: $uuids }, status: { _in: $openStatuses } }
      _set: {
        order_notes: $order_notes
        updated_at: $updatedAt
        updated_by_uuid: $updatedBy
      }
    ) {
      returning {
        uuid
        order_notes
      }
    }
  }
`;

export const GET_VARIANT_PACK_SUBSTITUTIONS = gql`
  query OrderInbound_GetVariantPackSubstitutions(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierId: uuid!
    $variantPackIds: [uuid!]!
  ) {
    getVariantPackSubstitutions(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierId
      variant_pack_ids: $variantPackIds
    ) {
      variant_pack_id
      substitutions {
        variant_pack_order_counts {
          days
          order_count
        }
        variant_pack {
          ...ItemsContext_VariantsPackFields
        }
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const useMemorizeOrder = () => {
  const updateInboundListQueryCache = useUpdateInboundListQueryCache();

  return useMutation<
    OrderInbounds_MemorizeOrderMutation,
    OrderInbounds_MemorizeOrderMutationVariables
  >(MEMORIZE_ORDER_MUTATION, {
    update: (client, result) => {
      updateInboundListQueryCache(client, (cachedData) => {
        const memorizedOrderUUID =
          result.data?.memorizeSmartOrder.smart_order_id;
        if (!memorizedOrderUUID) {
          return cachedData;
        }
        return {
          ...cachedData,
          smart_order: cachedData.smart_order.filter(
            (order) => order.uuid !== memorizedOrderUUID,
          ),
        };
      });
    },
  });
};

export const useUpdateSmartOrderItem = () => {
  const updateInboundListQueryCache = useUpdateInboundListQueryCache();
  return useMutation<
    OrderInput_UpdateSmartOrderItemMutation,
    OrderInput_UpdateSmartOrderItemMutationVariables
  >(UPDATE_SMART_ORDER_ITEM_MUTATION, {
    update: (client, result) => {
      updateInboundListQueryCache(client, (cachedData) => {
        const updatedInput =
          result.data?.updateSearchInput?.smart_order_search_input;
        if (!updatedInput) {
          return cachedData;
        }
        const smartOrderUUID = updatedInput.smart_order_uuid;
        return {
          ...cachedData,
          smart_order: cachedData.smart_order.map((order) =>
            order.uuid === smartOrderUUID
              ? {
                  ...order,
                  search_inputs: order.search_inputs.map((input) =>
                    input.uuid === updatedInput.uuid ? updatedInput : input,
                  ),
                }
              : order,
          ),
        };
      });
    },
  });
};

export const useCreateSearchTermCorrection = () => {
  return useMutation<
    OrderInput_CreateSearchTermCorrectionMutation,
    OrderInput_CreateSearchTermCorrectionMutationVariables
  >(CREATE_SEARCH_TERM_CORRECTION_MUTATION);
};

export const useSmartOrderSearchTermCorrections = (
  variables: OrderInbounds_SearchTermCorrectionsQueryVariables,
  skip: boolean,
) => {
  return useQuery<
    OrderInbounds_SearchTermCorrectionsQuery,
    OrderInbounds_SearchTermCorrectionsQueryVariables
  >(SMART_ORDER_SEARCH_TERM_CORRECTIONS_QUERY, {
    fetchPolicy: 'network-only',
    skip,
    variables,
  });
};

export const useUpdateInboundQuantity = () => {
  return useMutation<
    OrderInbound_UpdateQuantityMutation,
    OrderInbound_UpdateQuantityMutationVariables
  >(UPDATE_INBOUND_QUANTITY_MUTATION);
};

export const useUpdateInboundItemNotes = () => {
  return useMutation<
    OrderInbound_UpdateItemNotesMutation,
    OrderInbound_UpdateItemNotesMutationVariables
  >(UPDATE_INBOUND_ITEM_NOTES_MUTATION);
};

export const useAssignSmartOrder = () => {
  return useMutation<
    OrderInbound_AssignSmartOrderMutation,
    OrderInbound_AssignSmartOrderMutationVariables
  >(ASSIGN_SMART_ORDER_MUTATION, {
    context: {
      noBatch: true,
    },
  });
};

export const useUpdateAssignedEmployee = () => {
  const [updateAssignedEmployee] = useMutation<
    OrderInbound_UpdateSmartOrderAssigneeMutation,
    OrderInbound_UpdateSmartOrderAssigneeMutationVariables
  >(UPDATE_SMART_ORDER_ASSIGNEE_MUTATION, {
    context: {
      noBatch: true,
    },
  });
  // The mutation is an action returning bare ids, not a typed smart_order
  // object, so Apollo can't auto-patch the cache -- re-fetch by id instead.
  const [refreshAssignedOrders] = useLazyQuery<
    OrderInbounds_ListLiveUpdateQuery,
    OrderInbounds_ListLiveUpdateQueryVariables
  >(ORDER_INBOUNDS_LIST_LIVE_UPDATE_QUERY, {
    fetchPolicy: 'network-only',
  });

  return useCallback(
    // smartOrderUUIDs is one order, or one merge group's ids (every call site
    // in this app produces one or the other, never a mix of groups) -- the
    // action fans out server-side to the rest of the pivot's merge group, so
    // any id in the group is an equally valid pivot to send.
    async (input: {
      employeeUUID: string | null;
      smartOrderUUIDs: string[];
      supplierUUID: string;
    }) => {
      const [smartOrderUUID] = input.smartOrderUUIDs;
      if (!smartOrderUUID) {
        throw new Error(
          'useUpdateAssignedEmployee called with an empty smartOrderUUIDs array',
        );
      }
      const { data } = await updateAssignedEmployee({
        variables: {
          employeeUUID: input.employeeUUID,
          smartOrderUUID,
          supplierUUID: input.supplierUUID,
        },
      });
      try {
        await refreshAssignedOrders({
          variables: {
            businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
            orderUUIDs:
              data?.updateSmartOrderAssignee.smart_order_ids ??
              input.smartOrderUUIDs,
          },
        });
      } catch {
        // Best-effort refresh; the assignment itself already succeeded.
      }
    },
    [refreshAssignedOrders, updateAssignedEmployee],
  );
};

export const GET_SMART_ORDER_ASSIGNABLE_EMPLOYEES_QUERY = gql`
  query OrderInbound_GetSmartOrderAssignableEmployees(
    $smartOrderUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getSmartOrderAssignableEmployees(
      smart_order_id: $smartOrderUUID
      supplier_id: $supplierUUID
    ) {
      assignable_employees {
        employee_id
        group
        has_restaurant_access
        name
        submitted_recently
      }
    }
  }
`;

// Lazy: fetched when the assign dropdown opens, not on mount -- a list of
// inbounds can render many of these buttons, and the roster and its recency
// flags are only worth fetching once a rep is actually about to assign.
export const useSmartOrderAssignableEmployees = () =>
  useLazyQuery<
    OrderInbound_GetSmartOrderAssignableEmployeesQuery,
    OrderInbound_GetSmartOrderAssignableEmployeesQueryVariables
  >(GET_SMART_ORDER_ASSIGNABLE_EMPLOYEES_QUERY, {
    fetchPolicy: 'network-only',
  });

export const GRANT_SMART_ORDER_RESTAURANT_ACCESS_MUTATION = gql`
  mutation OrderInbound_GrantSmartOrderRestaurantAccess(
    $employeeUUID: uuid!
    $smartOrderUUID: uuid!
    $supplierUUID: uuid!
  ) {
    grantSmartOrderRestaurantAccess(
      employee_id: $employeeUUID
      smart_order_id: $smartOrderUUID
      supplier_id: $supplierUUID
    ) {
      access_granted
      employee_id
      has_restaurant_access
    }
  }
`;

export const useGrantSmartOrderRestaurantAccess = () =>
  useMutation<
    OrderInbound_GrantSmartOrderRestaurantAccessMutation,
    OrderInbound_GrantSmartOrderRestaurantAccessMutationVariables
  >(GRANT_SMART_ORDER_RESTAURANT_ACCESS_MUTATION, {
    context: {
      noBatch: true,
    },
  });

export const useUpdateInboundFulfillmentOptions = () => {
  return useMutation<
    OrderInbound_UpdateFulfillmentOptionsMutation,
    OrderInbound_UpdateFulfillmentOptionsMutationVariables
  >(UPDATE_INBOUND_FULFILLMENT_OPTIONS_MUTATION);
};

export const useUpdateInboundPoNumber = () => {
  return useMutation<
    OrderInbound_UpdatePoNumberMutation,
    OrderInbound_UpdatePoNumberMutationVariables
  >(UPDATE_INBOUND_PO_NUMBER_MUTATION);
};

export const useUpdateInboundOrderNotes = () => {
  return useMutation<
    OrderInbound_UpdateOrderNotesMutation,
    OrderInbound_UpdateOrderNotesMutationVariables
  >(UPDATE_INBOUND_ORDER_NOTES_MUTATION);
};
